import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import { visuallyHidden } from '@mui/utils';
import { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { API_KEY } from '../../utils/constants';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { Typography, useMediaQuery } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';


function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}


function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'Player',
    numeric: false,
    label: 'Player',
  },
  {
    id: 'Team',
    numeric: false,
    label: 'Team',
  },
  {
    id: 'Stat',
    numeric: true,
    label: 'Average Stat',
  },
  {
    id: 'Prop_Name',
    numeric: false,
    label: 'Prop Name',
  },
  {
    id: 'Line',
    numeric: true,
    label: 'Line',
  },
  {
    id: 'ExpectedMins',
    numeric: true,
    label: 'Mins',
  },
  {
    id: 'ExpectedStat',
    numeric: true,
    label: 'EXP Stat',
  },
  {
    id: 'Diff',
    numeric: true,
    label: 'Diff',
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy,  onRequestSort } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

export default function EnhancedTable() {


  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const isMediumScreen = useMediaQuery('(min-width:601px) and (max-width:960px)');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');
  const [isLoading, setIsLoading] = useState(false)
  const [propName, setPropName] = useState('');
  const [data, setData] = useState([])
  const [teams, setTeams] = useState([])
  const page = 0;
  const rowsPerPage = 400;
  

  const [checkedItems, setCheckedItems] = useState(teams);

  const handleCheckboxChange = (item) => {
    if (checkedItems.includes(item)) {
      // Item is already checked, so uncheck it
      setCheckedItems(checkedItems.filter((checkedItem) => checkedItem !== item));
    } else {
      // Item is not checked, so check it
      setCheckedItems([...checkedItems, item]);
    }
  }

  const checkAll = () => {
    if (checkedItems.length === teams.length) {
      setCheckedItems([]);
    } else {
      setCheckedItems(teams);
    }
  
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        const response = await axios.get(`${API_KEY}/dkPrizepicks`)
        setData(response.data.prizepicks)
        setTeams(response.data.teams)
        setCheckedItems(response.data.teams)
    }
      catch (error) {
        console.error(error)
      }finally {
        setIsLoading(false)
      }
  }
    fetchData()
  }, [])

  const getTableMaxHeight = () => {
    if (isSmallScreen) {
      return '450px'; // Set your maxHeight for small screens
    } else if (isMediumScreen) {
      return '500px'; // Set your maxHeight for medium screens
    } else {
      return '550px'; // Set your default maxHeight for larger screens
    }
  };

  const convertToCsv = () => {
    const csvString = [
      Object.keys(data[0]).join(','),
      ...data.map((row) => Object.values(row).join(',')),
    ].join('\n');

    // Create a Blob with the CSV string
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });

    // Create a download link
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'prizepicksProps.csv';
    link.click();

    // Cleanup
    window.URL.revokeObjectURL(link.href);
  };

  function DropDownMenu() {
    const handleChange = (event) => {
      setPropName(event.target.value);
    };
  
    return (
      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
        <InputLabel id="demo-select-small-label">Prop Name</InputLabel>
        <Select
          labelId="demo-select-small-label"
          id="demo-select-small"
          value={propName}
          label="Prop Name"
          onChange={handleChange}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          <MenuItem value={"Points"}>Points</MenuItem>
          <MenuItem value={"Rebounds"}>Rebounds</MenuItem>
          <MenuItem value={"Assists"}>Assists</MenuItem>
        </Select>
      </FormControl>
    );
  }

  function TeamMenu(){
    return (
      <div>
        <div>
        {teams.length === 0 ? <div>No teams to display</div> : <FormControlLabel
            label="All"
            control={
              <Checkbox
                checked={checkedItems.length === teams.length}
                indeterminate={checkedItems.length > 0 && checkedItems.length < teams.length}
                onChange={checkAll}
              />
            }
          />}
        </div>
        {teams.map((item) => (
          <FormControlLabel
            key={item}
            control={
              <Checkbox
                checked={checkedItems.includes(item)}
                onChange={() => handleCheckboxChange(item)}
              />
            }
            label={item}
          />
        ))}
      </div>
    );
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const visibleRows = useMemo(
    () =>
      stableSort(data, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [order, orderBy, page, rowsPerPage, data],
  );

  return (
    <>
    <Stack direction="row" spacing={1} sx={{mt: 5}}>
      <Typography variant="h4">Prizepicks</Typography>
      <DropDownMenu />
      {data.length > 0 &&
      <Tooltip title="Download CSV" placement='right'>
      <IconButton aria-label="delete" onClick={convertToCsv}>
        <CloudDownloadIcon />
      </IconButton>
      </Tooltip>
      }
      </Stack>
      <TeamMenu />
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer sx={{maxHeight: getTableMaxHeight()}}>
          <Table stickyHeader aria-labelledby="tableTitle"size={'small'}>
            <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} rowCount={data.length}/>
            <TableBody>
              {isLoading ? [0, 1, 2, 3, 4, 5, 6].map(() => {return<TableCell><Skeleton variant="rectangular" width={100} height={40} /></TableCell>}): 
              visibleRows.map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;
                if (row.Prop_Name === propName || propName === '' || propName === null){
                if (row.Team === checkedItems[0] || checkedItems.includes(row.Team)){
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={row.DkID}>
                    <TableCell component="th" id={labelId} scope="row" padding="1">{row.Player}</TableCell>
                    <TableCell>{row.Team}</TableCell>
                    <TableCell align="right">{row.Stat}</TableCell>
                    <TableCell>{row.Prop_Name}</TableCell>
                    <TableCell align="right">{row.Line}</TableCell>
                    <TableCell align="right">{row.ExpectedMins}</TableCell>
                    <TableCell align="right">{row.ExpectedStat}</TableCell>
                    <TableCell align="right">{row.Diff}</TableCell>
                  </TableRow>
              )}} return null})}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
    </>
  );
}